import type { ReactNode } from "react";
import type { FloorPhotoSearchCandidate } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { money } from "../../i18n/intl";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";

interface Props {
  candidate: FloorPhotoSearchCandidate;
  /** The first answer: drawn larger, under "Best match". */
  best: boolean;
  /** The kiosk's tap; without it the card is a picture of the answer (AI control's test, G-149). */
  onPick?: () => void;
  disabled?: boolean;
  /** Beside the name — AI control marks the florist's own pick. */
  badge?: ReactNode;
}

/**
 * G-146 — one item the photo search offers, as the kiosk shows it. Shared
 * with AI control's test view so a draft is judged on exactly the card a
 * florist would see.
 */
export function PhotoSearchCandidateCard({ candidate, best, onPick, disabled, badge }: Props) {
  const { t } = useT();
  const className = `sf-ps-cand${best ? " sf-ps-cand--best" : ""}`;
  const inner = (
    <>
      <span className="sf-ps-cand-thumb">
        <ShopFloorBouquetThumb id={candidate.id} imageKey={candidate.imageKey} alt={candidate.name} />
      </span>
      <span className="sf-ps-cand-main">
        <span className="sf-ps-cand-name">
          {candidate.name}
          {badge}
        </span>
        <span className="sf-ps-cand-reason">{candidate.reason}</span>
        <span className="sf-ps-cand-foot">
          <span className={`sf-ps-match sf-ps-match--${candidate.match.toLowerCase()}`}>
            {candidate.match === "EXACT" ? t("photoSearch.matchExact") : t("photoSearch.matchSimilar")}
          </span>
          <span className="sf-ps-cand-where">
            {candidate.places
              .map((p) => t("photoSearch.qtyInZone", { qty: p.qty, zone: p.zoneName }))
              .join(" · ")}
          </span>
        </span>
      </span>
      {candidate.saleUnitPriceEur ? (
        <span className="sf-ps-cand-price">{money(candidate.saleUnitPriceEur)}</span>
      ) : null}
    </>
  );
  if (!onPick) return <div className={className}>{inner}</div>;
  return (
    <button type="button" className={className} onClick={onPick} disabled={disabled}>
      {inner}
    </button>
  );
}
